import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CircleNotch, ArrowRight, Lightning, Fire, Target, ChartBar, TrendUp } from "@phosphor-icons/react";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WindowDrawDNA() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/analysis/draw-dna`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="draw-dna-loading">
      <CircleNotch size={40} className="animate-spin text-blue-600" />
    </div>
  );

  if (!data || data.error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-500">{data?.error || "Failed to load data"}</div>
    </div>
  );

  const { reference, matches, next_profile, similarity_tiers } = data;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="draw-dna-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="text-gray-500 hover:text-gray-900" data-testid="back-button">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">W60: Draw DNA Sequence Match</h1>
            <p className="text-xs text-gray-500">
              {data.total_sequences_searched?.toLocaleString()} sequences analyzed — weighted projection from all matching history
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6 space-y-5">


        <WindowNarrative windowId="W60" {...NARRATIVES.W60} className="mb-5" />
        {/* Reference draws: Previous → Current → ??? */}
        <ReferenceCards previous={reference.previous} current={reference.current} />

        {/* DNA Suggested Combos */}
        {data.suggested_combos && data.suggested_combos.length > 0 && (
          <SuggestedCombos combos={data.suggested_combos} />
        )}

        {/* Positional Range Analysis */}
        {next_profile?.pos_range_analysis && (
          <PositionalRangeAnalysis analysis={next_profile.pos_range_analysis} contributing={next_profile.contributing_sequences} />
        )}

        {/* Next Draw Projection */}
        {next_profile && <NextDrawProjection profile={next_profile} tiers={similarity_tiers} />}

        {/* Top 10 matches for reference */}
        {matches && matches.length > 0 && <MatchList matches={matches} />}
      </main>
    </div>
  );
}

function DrawCard({ draw, label, labelColor, showLS = true }) {
  const oe = `${draw.odds}O/${5 - draw.odds}E`;
  const hl = `${draw.high}H/${5 - draw.high}L`;
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1" data-testid={`draw-card-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
      <div className={`px-3 py-1.5 ${labelColor} flex items-center justify-between`}>
        <span className="text-[10px] font-black uppercase tracking-wider text-white">{label}</span>
        {draw.draw_number && <span className="text-[10px] font-mono text-white/70">#{draw.draw_number}</span>}
      </div>
      <div className="p-3">
        <div className="flex items-center gap-1.5 mb-2">
          {draw.balls.map((b, i) => (
            <span key={`b-${b}-${i}`} className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 text-white text-sm font-bold flex items-center justify-center">{b}</span>
          ))}
          {showLS && draw.ls && (
            <>
              <span className="text-gray-300 mx-0.5">+</span>
              {draw.ls.map((l, i) => (
                <span key={`l-${l}-${i}`} className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-sm font-bold flex items-center justify-center">{l}</span>
              ))}
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Tag label="Sum" value={draw.sum} />
          <Tag label="Spread" value={draw.spread} />
          <Tag label="O/E" value={oe} />
          <Tag label="H/L" value={hl} />
          <Tag label="Primes" value={draw.primes} />
          <Tag label="Boundary" value={draw.boundary} />
          <Tag label="Consec" value={draw.consecutive} />
          <Tag label="LS Sum" value={draw.ls_sum} />
        </div>
        {draw.draw_date && <div className="text-[9px] text-gray-400 mt-2">{draw.draw_date}</div>}
      </div>
    </div>
  );
}

function Tag({ label, value }) {
  return (
    <span className="text-[9px] bg-gray-100 rounded px-1.5 py-0.5">
      <span className="text-gray-400">{label}:</span> <span className="font-bold text-gray-700">{value}</span>
    </span>
  );
}

function ReferenceCards({ previous, current }) {
  return (
    <div className="flex gap-3 items-stretch" data-testid="reference-cards">
      <DrawCard draw={previous} label="Previous Draw" labelColor="bg-gray-600" />
      <div className="flex items-center"><ArrowRight size={24} weight="bold" className="text-gray-400" /></div>
      <DrawCard draw={current} label="Current Draw" labelColor="bg-blue-600" />
      <div className="flex items-center"><ArrowRight size={24} weight="bold" className="text-gray-400" /></div>
      <div className="flex-1 bg-gradient-to-br from-violet-600 to-indigo-700 rounded-lg flex flex-col items-center justify-center p-4 min-h-[140px]">
        <span className="text-3xl font-black text-white">?</span>
        <span className="text-[10px] text-violet-200 font-bold uppercase mt-1">What Comes Next?</span>
      </div>
    </div>
  );
}

function SuggestedCombos({ combos }) {
  const matchBadge = (val) => {
    if (val === "yes") return <span className="text-[8px] px-1 py-0.5 rounded bg-green-100 text-green-700 font-bold">MATCH</span>;
    if (val === "close") return <span className="text-[8px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">CLOSE</span>;
    return <span className="text-[8px] px-1 py-0.5 rounded bg-red-100 text-red-700 font-bold">OFF</span>;
  };

  return (
    <div className="bg-white border-2 border-emerald-400 rounded-lg overflow-hidden" data-testid="suggested-combos">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 flex items-center gap-2">
        <Lightning size={18} weight="fill" className="text-amber-300" />
        <div>
          <h3 className="text-sm font-black text-white uppercase tracking-wider">DNA-Matched Predictions</h3>
          <p className="text-[10px] text-emerald-200">3 combos generated to match the weighted projection from 1,932 historical sequences</p>
        </div>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-4">
          {combos.map((c) => (
            <div key={c.rank} className="border-2 border-emerald-200 rounded-xl overflow-hidden bg-gradient-to-b from-emerald-50/50 to-white" data-testid={`combo-${c.rank}`}>
              <div className="bg-emerald-100 px-3 py-2 flex items-center justify-between">
                <span className="font-black text-emerald-800 text-sm">Pick #{c.rank}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold">
                  DNA Score: {c.dna_score}
                </span>
              </div>
              <div className="p-3">
                {/* Ball numbers */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  {c.balls.map((b, i) => {
                    const pm = (c.pos_matches || []).find(p => p.ball === b);
                    return (
                      <div key={`cb-${b}-${i}`} className="flex flex-col items-center gap-0.5">
                        <span className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 text-white text-base font-bold flex items-center justify-center shadow-md">{b}</span>
                        {pm && <span className={`text-[7px] font-bold px-1 rounded ${pm.rank <= 2 ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>{pm.position} #{pm.rank}</span>}
                      </div>
                    );
                  })}
                  <span className="text-gray-300 mx-1 font-bold">+</span>
                  {c.lucky_stars.map((l, i) => (
                    <span key={`cl-${l}-${i}`} className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-base font-bold flex items-center justify-center shadow-md">{l}</span>
                  ))}
                </div>

                {/* Structural breakdown */}
                <div className="grid grid-cols-3 gap-1.5">
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">Sum</div>
                    <div className="font-mono font-bold text-sm">{c.sum}</div>
                    {matchBadge(c.sum_match)}
                  </div>
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">Spread</div>
                    <div className="font-mono font-bold text-sm">{c.spread}</div>
                    {matchBadge(c.spread_match)}
                  </div>
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">LS Sum</div>
                    <div className="font-mono font-bold text-sm">{c.ls_sum}</div>
                  </div>
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">O/E</div>
                    <div className="font-mono font-bold text-sm">{c.odds}O/{c.evens}E</div>
                  </div>
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">H/L</div>
                    <div className="font-mono font-bold text-sm">{c.high}H/{c.low}L</div>
                  </div>
                  <div className="bg-white rounded-lg p-1.5 text-center border border-gray-100">
                    <div className="text-[8px] text-gray-400 uppercase">Primes</div>
                    <div className="font-mono font-bold text-sm">{c.primes}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PositionalRangeAnalysis({ analysis, contributing }) {
  const ballLabels = {
    ball_1: "Ball 1", ball_2: "Ball 2", ball_3: "Ball 3",
    ball_4: "Ball 4", ball_5: "Ball 5",
  };
  const ballColors = {
    ball_1: { header: "bg-blue-600", band: "bg-blue-500", accent: "text-blue-700", light: "bg-blue-50", border: "border-blue-300" },
    ball_2: { header: "bg-emerald-600", band: "bg-emerald-500", accent: "text-emerald-700", light: "bg-emerald-50", border: "border-emerald-300" },
    ball_3: { header: "bg-amber-600", band: "bg-amber-500", accent: "text-amber-700", light: "bg-amber-50", border: "border-amber-300" },
    ball_4: { header: "bg-rose-600", band: "bg-rose-500", accent: "text-rose-700", light: "bg-rose-50", border: "border-rose-300" },
    ball_5: { header: "bg-violet-600", band: "bg-violet-500", accent: "text-violet-700", light: "bg-violet-50", border: "border-violet-300" },
  };

  return (
    <div className="bg-white border-2 border-indigo-400 rounded-lg overflow-hidden" data-testid="pos-range-analysis">
      <div className="bg-gradient-to-r from-indigo-700 to-blue-700 px-4 py-3">
        <h3 className="text-sm font-black text-white uppercase tracking-wider">Positional Range Analysis</h3>
        <p className="text-[10px] text-indigo-200">
          From {contributing?.toLocaleString()} weighted sequences — what range does each ball position fall into?
        </p>
      </div>
      <div className="p-4 space-y-4">
        {["ball_1", "ball_2", "ball_3", "ball_4", "ball_5"].map(bk => {
          const pos = analysis[bk];
          if (!pos) return null;
          const colors = ballColors[bk];
          const maxBandPct = Math.max(...pos.bands.map(b => b.pct), 1);

          return (
            <div key={bk} className={`border ${colors.border} rounded-lg overflow-hidden`}>
              {/* Header */}
              <div className={`${colors.header} px-4 py-2 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <span className="text-white font-black text-sm">{ballLabels[bk]}</span>
                  <span className="text-white/70 text-[10px]">
                    Avg: <b className="text-white">{pos.weighted_avg}</b> | Median: <b className="text-white">{pos.weighted_median}</b> | Range: {pos.min}-{pos.max}
                  </span>
                </div>
                {pos.dominant_band && (
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/20 text-white font-bold">
                    Peak: {pos.dominant_band.label} ({pos.dominant_band.pct}%)
                  </span>
                )}
              </div>

              <div className="p-3 grid grid-cols-2 gap-4">
                {/* Band distribution (histogram) */}
                <div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-2">Range Distribution</div>
                  <div className="space-y-1">
                    {pos.bands.map((b, i) => {
                      const barW = Math.max(4, (b.pct / maxBandPct) * 100);
                      const isDominant = pos.dominant_band && b.label === pos.dominant_band.label;
                      return (
                        <div key={`pb-${b.label}-${i}`} className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono w-12 text-right ${isDominant ? colors.accent + " font-black" : "text-gray-500 font-bold"}`}>{b.label}</span>
                          <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                            <div className={`h-full rounded ${isDominant ? colors.band : "bg-gray-300"} transition-all flex items-center`} style={{ width: `${barW}%` }}>
                              {b.pct >= 5 && <span className="text-[8px] text-white font-bold ml-1">{b.pct}%</span>}
                            </div>
                          </div>
                          {b.pct < 5 && <span className="text-[9px] text-gray-400 w-10">{b.pct}%</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Key threshold table */}
                <div>
                  <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-2">Threshold Probabilities</div>
                  <div className="space-y-0.5">
                    {pos.buckets.map((t, i) => {
                      const belowStrong = t.below_pct >= 60;
                      const aboveStrong = t.above_pct >= 60;
                      return (
                        <div key={`bkt-${t.threshold}-${i}`} className="flex items-center text-[10px] gap-1">
                          <span className="font-mono text-gray-600 w-12 text-right font-bold">&le; {t.threshold}</span>
                          <div className="flex-1 flex items-center gap-1">
                            <div className="flex-1 h-3.5 bg-gray-100 rounded-l overflow-hidden">
                              <div className={`h-full ${belowStrong ? colors.band : "bg-gray-300"} rounded-l flex items-center justify-end`} style={{ width: `${t.below_pct}%` }}>
                                {t.below_pct >= 10 && <span className="text-[7px] text-white font-bold mr-0.5">{t.below_pct}%</span>}
                              </div>
                            </div>
                            <div className="flex-1 h-3.5 bg-gray-100 rounded-r overflow-hidden">
                              <div className={`h-full ${aboveStrong ? colors.band + " opacity-60" : "bg-gray-300"} rounded-r flex items-center`} style={{ width: `${t.above_pct}%` }}>
                                {t.above_pct >= 10 && <span className="text-[7px] text-white font-bold ml-0.5">{t.above_pct}%</span>}
                              </div>
                            </div>
                          </div>
                          <span className="font-mono text-gray-600 w-10 font-bold">&gt; {t.threshold}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NextDrawProjection({ profile, tiers }) {
  return (
    <div className="bg-white border-2 border-violet-400 rounded-lg overflow-hidden" data-testid="next-projection">
      <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightning size={18} weight="fill" className="text-amber-300" />
          <div>
            <h3 className="text-sm font-black text-white uppercase tracking-wider">Next Draw Projection</h3>
            <p className="text-[10px] text-violet-200">
              Weighted projection from <b>{profile.contributing_sequences.toLocaleString()}</b> historical sequences (closer matches weighted higher)
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-4">
        {/* Similarity tier breakdown */}
        {tiers && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-2">Data Quality — Similarity Distribution</div>
            <div className="flex gap-1.5">
              {tiers.map((t, i) => {
                const colors = ["bg-green-500", "bg-emerald-400", "bg-blue-400", "bg-amber-400", "bg-orange-400", "bg-gray-300"];
                const barH = Math.max(4, Math.min(60, t.pct * 1.5));
                return (
                  <div key={`tier2-${t.label}-${i}`} className="flex-1 flex flex-col items-center gap-0.5">
                    <span className="text-[8px] font-bold text-gray-500">{t.count}</span>
                    <div className={`w-full rounded-t ${colors[i]}`} style={{ height: barH }} />
                    <span className="text-[7px] text-gray-400 text-center leading-tight">{t.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Structural averages */}
        <div className="grid grid-cols-5 gap-3">
          {[
            { label: "Expected Sum", value: profile.avg_sum, range: profile.sum_range, p10p90: profile.sum_p10_p90 },
            { label: "Expected Spread", value: profile.avg_spread, range: profile.spread_range, p10p90: profile.spread_p10_p90 },
            { label: "Avg Odds/Draw", value: profile.avg_odds },
            { label: "Avg High/Draw", value: profile.avg_high },
            { label: "Expected LS Sum", value: profile.avg_ls_sum, range: profile.ls_sum_range },
          ].map((m, i) => (
            <div key={`avg-${m.label}`} className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-center">
              <div className="text-[9px] text-violet-500 uppercase font-bold tracking-wider">{m.label}</div>
              <div className="font-mono font-bold text-2xl text-gray-900">{m.value}</div>
              {m.p10p90 && <div className="text-[9px] text-gray-500 mt-0.5">80th pctl: {m.p10p90[0]}-{m.p10p90[1]}</div>}
              {m.range && <div className="text-[8px] text-gray-400">Full: {m.range[0]}-{m.range[1]}</div>}
            </div>
          ))}
        </div>

        {/* O/E and H/L distributions side by side */}
        <div className="grid grid-cols-2 gap-3">
          <DistributionBar title="Odd/Even Pattern" items={profile.oe_distribution} />
          <DistributionBar title="High/Low Pattern" items={profile.hl_distribution} />
        </div>

        {/* Hot balls & LS */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Fire size={14} weight="fill" className="text-red-500" />
              <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">Hot Balls (Weighted from {profile.contributing_sequences} sequences)</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {profile.hot_balls.slice(0, 15).map((b, i) => (
                <div key={b.number} className={`flex flex-col items-center gap-0.5 rounded-lg px-2 py-1.5 ${i < 3 ? "bg-red-100 border border-red-300" : i < 7 ? "bg-orange-50 border border-orange-200" : "bg-white border border-gray-200"}`}>
                  <span className={`font-mono font-bold text-sm ${i < 3 ? "text-red-700" : i < 7 ? "text-orange-700" : "text-gray-700"}`}>{b.number}</span>
                  <span className="text-[8px] text-gray-400">{b.pct}%</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Fire size={14} weight="fill" className="text-amber-500" />
              <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">Hot Lucky Stars</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {profile.hot_ls.map((l, i) => (
                <div key={l.number} className={`flex flex-col items-center gap-0.5 rounded-lg px-2.5 py-1.5 ${i < 2 ? "bg-amber-100 border border-amber-300" : "bg-white border border-gray-200"}`}>
                  <span className={`font-mono font-bold text-sm ${i < 2 ? "text-amber-700" : "text-gray-700"}`}>{l.number}</span>
                  <span className="text-[8px] text-gray-400">{l.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Positional Top Numbers */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Target size={14} weight="fill" className="text-indigo-600" />
            <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">Positional Frequency (Weighted — which number appeared most at each position)</span>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {["ball_1", "ball_2", "ball_3", "ball_4", "ball_5"].map((bk, idx) => (
              <div key={bk} className="bg-white border border-gray-200 rounded-lg p-2">
                <div className="text-[9px] font-bold text-gray-500 uppercase mb-1.5 text-center">B{idx + 1}</div>
                <div className="space-y-1">
                  {(profile.positional_top[bk] || []).slice(0, 8).map((p, i) => {
                    const maxW = profile.positional_top[bk]?.[0]?.pct || 1;
                    const barW = Math.max(8, (p.pct / maxW) * 100);
                    return (
                      <div key={p.number} className="flex items-center gap-1.5">
                        <span className={`font-mono font-bold text-xs w-6 text-right ${i === 0 ? "text-indigo-700" : "text-gray-600"}`}>{p.number}</span>
                        <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${i === 0 ? "bg-indigo-500" : i < 3 ? "bg-indigo-300" : "bg-gray-300"}`} style={{ width: `${barW}%` }} />
                        </div>
                        <span className="text-[8px] text-gray-400 w-8">{p.pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: "Avg Primes", value: profile.avg_primes },
            { label: "Avg Boundary", value: profile.avg_boundary },
            { label: "Avg Consecutive", value: profile.avg_consecutive },
            { label: "Avg Mult of 3", value: profile.avg_mult3 },
          ].map((m, i) => (
            <div key={`gauge-${m.label}`} className="bg-gray-50 rounded-lg p-2.5 text-center">
              <div className="text-[8px] text-gray-400 uppercase font-bold">{m.label}</div>
              <div className="font-mono font-bold text-sm text-gray-700">{m.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DistributionBar({ title, items }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-2">
        <ChartBar size={14} className="text-gray-500" />
        <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wider">{title}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <div key={item.pattern} className="flex items-center gap-2">
            <span className={`text-[10px] font-bold w-14 ${i === 0 ? "text-indigo-700" : "text-gray-600"}`}>{item.pattern}</span>
            <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${i === 0 ? "bg-indigo-500" : "bg-gray-400"}`} style={{ width: `${item.pct}%` }} />
            </div>
            <span className="text-[10px] font-mono text-gray-500 w-10 text-right">{item.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchList({ matches }) {
  if (!matches || matches.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="match-list">
      <div className="px-4 py-2.5 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendUp size={16} weight="bold" className="text-blue-600" />
          <div>
            <h3 className="font-bold text-sm text-gray-800">Top 10 Closest Matches (Reference)</h3>
            <p className="text-[10px] text-gray-500">The best-matching historical sequences — projection above uses ALL sequences weighted by closeness</p>
          </div>
        </div>
        <span className="text-[10px] text-gray-400">{matches.length} matches shown</span>
      </div>
      <div className="divide-y divide-gray-100">
        {matches.map((m, idx) => (
          <MatchRow key={`m-${m.draw_n?.draw_number || idx}-${m.draw_n1?.draw_number || idx}`} match={m} rank={idx + 1} />
        ))}
      </div>
    </div>
  );
}

function MatchRow({ match, rank }) {
  const { draw_n, draw_n1, draw_next, combined_score, prev_score, curr_score } = match;
  const scoreBg = combined_score <= 5 ? "bg-green-600" : combined_score <= 10 ? "bg-amber-500" : combined_score <= 15 ? "bg-orange-500" : "bg-red-500";

  return (
    <div className={`px-4 py-3 ${rank <= 3 ? "bg-indigo-50/30" : ""}`} data-testid={`match-row-${rank}`}>
      <div className="flex items-center gap-3 mb-2">
        <span className={`w-8 h-8 rounded-lg ${scoreBg} text-white text-xs font-black flex items-center justify-center`}>#{rank}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] px-2 py-0.5 rounded bg-gray-100 font-bold text-gray-500">
            Match: {combined_score}
          </span>
          <span className="text-[9px] text-gray-400">
            (prev: {prev_score} + curr: {curr_score})
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {/* Matched Previous */}
        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
          <div className="text-[8px] text-gray-400 uppercase font-bold mb-1">Matched "Previous" — Draw #{draw_n.draw_number}</div>
          <div className="flex gap-1 mb-1">
            {draw_n.balls.map((b, i) => (
              <span key={`mn-b-${b}-${i}`} className="w-7 h-7 rounded-full bg-gray-600 text-white text-[10px] font-bold flex items-center justify-center">{b}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            <MiniTag label="S" value={draw_n.sum} />
            <MiniTag label="Sp" value={draw_n.spread} />
            <MiniTag label="O/E" value={`${draw_n.odds}/${5 - draw_n.odds}`} />
            <MiniTag label="H/L" value={`${draw_n.high}/${5 - draw_n.high}`} />
          </div>
        </div>

        {/* Matched Current */}
        <div className="bg-blue-50/50 rounded-lg p-2 border border-blue-200">
          <div className="text-[8px] text-blue-500 uppercase font-bold mb-1">Matched "Current" — Draw #{draw_n1.draw_number}</div>
          <div className="flex gap-1 mb-1">
            {draw_n1.balls.map((b, i) => (
              <span key={`mn1-b-${b}-${i}`} className="w-7 h-7 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">{b}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            <MiniTag label="S" value={draw_n1.sum} />
            <MiniTag label="Sp" value={draw_n1.spread} />
            <MiniTag label="O/E" value={`${draw_n1.odds}/${5 - draw_n1.odds}`} />
            <MiniTag label="H/L" value={`${draw_n1.high}/${5 - draw_n1.high}`} />
          </div>
        </div>

        {/* What Came Next */}
        <div className="bg-violet-50 rounded-lg p-2 border-2 border-violet-400">
          <div className="text-[8px] text-violet-600 uppercase font-bold mb-1 flex items-center gap-1">
            <Lightning size={10} weight="fill" />
            What Came Next — Draw #{draw_next.draw_number}
          </div>
          <div className="flex gap-1 mb-1">
            {draw_next.balls.map((b, i) => (
              <span key={`mnext-b-${b}-${i}`} className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white text-[10px] font-bold flex items-center justify-center">{b}</span>
            ))}
            <span className="text-gray-300 text-xs mx-0.5">+</span>
            {draw_next.ls.map((l, i) => (
              <span key={`mnext-l-${l}-${i}`} className="w-7 h-7 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white text-[10px] font-bold flex items-center justify-center">{l}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            <MiniTag label="S" value={draw_next.sum} />
            <MiniTag label="Sp" value={draw_next.spread} />
            <MiniTag label="O/E" value={`${draw_next.odds}/${5 - draw_next.odds}`} />
            <MiniTag label="H/L" value={`${draw_next.high}/${5 - draw_next.high}`} />
          </div>
          {draw_next.draw_date && <div className="text-[8px] text-gray-400 mt-1">{draw_next.draw_date}</div>}
        </div>
      </div>
    </div>
  );
}

function MiniTag({ label, value }) {
  return (
    <span className="text-[8px] bg-white/80 rounded px-1 py-0.5">
      <span className="text-gray-400">{label}:</span><span className="font-bold text-gray-700">{value}</span>
    </span>
  );
}
